"use no memo";

import axios from "axios";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";
import classNames from "classnames";

import { API_URLS } from "src/utils/API_URLS";
import { capitalizeFirstLetter, showErrorToast } from "src/utils/methods";
import { markCreatorAsViewed } from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/CreatorSearch/redux/creatorSearchActions";
import "./ProfileDataPane.scss";

import { Pane } from "src/app/components/Pane/Pane";
import {
  PaneTopbar,
  PaneTopbarCol,
  PaneTopbarToolbar,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import SocialProviderSelect from "src/app/Task/tabs/ProfileDataTab/components/SocialProviderSelect";
import Loader from "src/app/components/Loader/Loader";
import { PaneContainer } from "src/app/components/Pane/PaneContainer/PaneContainer";

import { Button } from "src/app/components/Button/Button";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { showToast } from "src/app/methods/showToast";
import AddCreatorsCDTWorkspaceProjectModal from "src/app/modals/AddCreatorsCDTWorkspaceProjectModal/AddCreatorsCDTWorkspaceProjectModal";
import { setCreateNewProjectData } from "src/redux/main/mainActions";
import { ReactComponent as CreatorIcon } from "src/images/creator-discovery.svg";
import { ReactComponent as ListIcon } from "src/images/list.svg";
import { ReactComponent as PlusIcon } from "src/images/plus-white.svg";
import { ReactComponent as RocketIcon } from "src/images/rocket-grey.svg";
import { ReactComponent as TickIcon } from "src/images/tick-blue.svg";
import { ReactComponent as RefreshIcon } from "src/images/refresh1.svg";
import {
  clearGlobalTaskDetails,
  getGlobalTaskDetails,
} from "src/redux/creator-database/creatorDatabaseActions";
import { uuidv7 as uuid } from "uuidv7";
import { navigateToCreateNewProject } from "src/app/SettingsModal/methods";
import { PreviewSettingsDropdown } from "src/app/components/GlobalTaskDetails/PreviewSettingsDropdown/PreviewSettingsDropdown";
import { CreatorShowcaseContext } from "src/app/CreatorShowcase/utils";
import Tabs from "src/app/components/Tabs/Tabs";
import Tab from "src/app/components/Tabs/Tab";
import {
  Performance,
  PerformanceContext,
} from "src/app/components/GlobalTaskDetails/tabs/Performance/Performance";
import { GlobalActivityTab } from "src/app/components/GlobalTaskDetails/tabs/GlobalActivityTab/GlobalActivityTab";
import { convertToLocalTimezone } from "src/app/methods/convertToLocalTimezone";
import { GlobalData } from "src/app/components/GlobalTaskDetails/tabs/GlobalData/GlobalData";
import {
  InitialPerformanceFilters,
  PerformanceFilters,
} from "src/app/components/GlobalTaskDetails/tabs/Performance/components/PerformanceFilters/PerformanceFilters";
import { PerformanceFiltersButton } from "src/app/components/GlobalTaskDetails/tabs/Performance/components/PerformanceFilters/components/PerformanceFiltersButton/PerformanceFiltersButton";
import { TaskSocialData } from "src/app/Task/tabs/TaskSocialData/TaskSocialData";
import { UserNotFound } from "./partials/UserNotFound/UserNotFound";
import UserInfo from "./partials/UserInfo/UserInfo";
import useInterval from "../../../utils/useInterval";
import CONSTS from "../../helpers/consts";
import { ExportDropdown } from "./partials/ExportDropdown/ExportDropdown";
import {
  deleteInfluencersFromList,
  getInfluencersListData,
} from "../../audiences-manager/audiencesManagerActions";
import {
  generateSelectedInfluencersWithSocialProfiles,
  selectedCreatorsPayload,
} from "../../audiences-manager/views/CreatorDiscoveryToolActionBar";
import { ActiveProfileDataTab } from "./utils";
import { useShowPane } from "src/hooks/useShowPane";
import { usePageTitle } from "src/app/title-management/usePageTitle";
import {
  CreatorTags,
  CreatorTagsContext,
} from "src/app/components/CreatorTags/CreatorTags";

const PANE_WIDTH = 798;

function ProfileDataPane({
  activeTab,
  listId,
  refreshAudience,
  addToAudience,
  setSelectedInfluencers,
  influencersInList,
  searchFilters,
  projectId = "",
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const activeWorkspaceUuid = useSelector(
    (state) => state.mainReducer.activeWorkspaceUuid,
  );
  const globalTaskDetails = useSelector(
    (state) => state.creatorDatabaseReducer.globalTaskDetails,
  );
  const membersList = useSelector((state) => state.projectReducer.membersList);

  const { socialProvider, socialUsername } = useParams();
  const searchParams = new URLSearchParams(location.search);

  const [selectedSocialProvider, setSelectedSocialProvider] = useState({
    value: socialProvider,
    label: (
      <IDHFormattedMessage
        id={`front_${socialProvider}`}
        defaultMessage={capitalizeFirstLetter(socialProvider)}
      />
    ),
  });
  const [profileData, setProfileData] = useState(null);
  const [originProfile, setOriginProfile] = useState(null);
  const [paneState, setPaneState] = useState("loading");
  const [activeProfileTab, setActiveProfileTab] = useState(
    ActiveProfileDataTab.SocialData,
  );
  const [performanceData, setPerformanceData] = useState([]);
  const [performanceFilters, setPerformanceFilters] = useState(
    InitialPerformanceFilters,
  );
  const [showFilters, setShowFilters] = useState(false);
  const [dateCreated, setDateCreated] = useState(null);
  const [
    showModalAddCreatorsToWorkspaceProject,
    setShowModalAddCreatorsToWorkspaceProject,
  ] = useState(false);

  const selectedInfluencersUrls = [profileData?.user_data?.socialProfileUrl];
  const userData = [profileData?.user_data];
  const disableAddToAudience = Boolean(
    influencersInList?.find((item) => item.userId === profileData?.hashUserId),
  );
  const [isUpdatingProfileData, setUpdatingProfileData] = useState(false);
  const attempts = useRef(10);
  const [shouldSync, setShouldSync] = useState(false);
  const [
    shouldDisplayUpdateProfileDataButton,
    setShouldDisplayUpdateProfileDataButton,
  ] = useState(false);

  const creatorId = profileData?.user_data?.socialProfileUrl || "";

  usePageTitle(profileData?.user_data?.username);

  useEffect(() => {
    if (profileData?.wsGlobalTaskUuid) {
      dispatch(getGlobalTaskDetails(profileData.wsGlobalTaskUuid));
    }
  }, [profileData?.wsGlobalTaskUuid]);

  useEffect(() => {
    if (globalTaskDetails?.created) {
      const dateValue = globalTaskDetails.created.date;
      if (dateValue !== null) {
        setDateCreated(convertToLocalTimezone(dateValue));
      }
    }
  }, [globalTaskDetails?.created]);

  const taskOwner = useMemo(() => {
    return membersList?.find(
      (member) => member.id === globalTaskDetails?.ownerWsMemberUuid,
    );
  }, [globalTaskDetails?.ownerWsMemberUuid, membersList]);

  const checkProfileData = async () => {
    if (attempts.current > 0) {
      try {
        const {
          data: {
            content: { hasFreshData },
          },
        } = await axios.post(CONSTS.checkProfileData, {
          hashUserId: profileData.hashUserId,
          requestTime: profileData.lastUserSync.requestTime.date,
        });
        if (hasFreshData) {
          setShouldDisplayUpdateProfileDataButton(true);
          setShouldSync(false);
        }
        if (attempts.current === 1) {
          // Interval will be cleared
          setShouldSync(false);
        }
      } catch (e) {
        console.error(e);
      }
      attempts.current -= 1;
    }
  };

  useInterval(
    () => {
      checkProfileData();
    },
    shouldSync ? 5000 : null,
  );

  const closePane = () => {
    const pathNameWithoutProfile =
      window.location.pathname.split("/profile/")[0];

    searchParams.delete("origin");

    history.push({
      pathname: pathNameWithoutProfile,
      search: `?${searchParams.toString()}`,
    });
  };

  const [showPane, hidePane] = useShowPane(closePane);

  const getSocialUsernameByProvider = (provider) =>
    originProfile
      ? originProfile.user_data.socialProviders[provider].socialUsername
      : socialUsername;

  const openProfile = () => {
    const socialUsername = getSocialUsernameByProvider(
      selectedSocialProvider.value,
    );
    const pathNameWithoutProfile =
      window.location.pathname.split("/profile/")[0];
    const pathNameWithProfile =
      `${pathNameWithoutProfile}/profile/` +
      `${selectedSocialProvider.value}/${socialUsername}`;

    const originParams = searchParams.get("origin");
    const originProvider = originParams?.split("/")[0];

    if (!originParams)
      searchParams.set(
        "origin",
        `${socialProvider}/${getSocialUsernameByProvider(socialProvider)}`,
      );

    if (originProvider === selectedSocialProvider.value)
      searchParams.delete("origin");

    history.push({
      pathname: pathNameWithProfile,
      search: `?${searchParams.toString()}`,
    });
  };

  const getOriginData = async () => {
    const originParams = searchParams.get("origin");
    const originProvider = originParams.split("/")[0];
    const originUsername = originParams.split("/")[1];

    const originUrl = API_URLS.getProfileData
      .replace(":workspaceUuid:", activeWorkspaceUuid)
      .replace(":socialProvider:", originProvider.replace("_", ""))
      .replace(":socialUsername:", originUsername);

    if (!originProfile) {
      axios.get(originUrl).then((r) => setOriginProfile(r.data.content));
    }
  };

  const getProfileData = async (socialProvider) => {
    const url = API_URLS.getProfileData
      .replace(":workspaceUuid:", activeWorkspaceUuid)
      .replace(":socialProvider:", socialProvider.replace("_", ""))
      .replace(":socialUsername:", socialUsername);

    setPaneState("loading");
    return axios
      .get(url, projectId ? { headers: { "Project-Uuid": projectId } } : undefined)
      .then((r) => {
        const originData = searchParams.get("origin");
        setProfileData(r.data.content);

        if (!originProfile && !originData) setOriginProfile(r.data.content);
        setPaneState("origin");

        const { shouldProfileBeRefreshed } = r.data.content.lastUserSync;
        if (shouldProfileBeRefreshed) {
          attempts.current = 10;
          setShouldSync(true);
        }

        if (projectId && r.data.content.hashUserId) {
          dispatch(markCreatorAsViewed(r.data.content.hashUserId));
        }

        return r?.data?.content?.wsGlobalTaskUuid;
      })
      .catch((err) => {
        console.error(err);
        const originParams = searchParams.get("origin");

        if (originParams) {
          setPaneState("notFoundWithOrigin");
        } else setPaneState("notFound");
      });
  };

  const getContextForShowcase = () => {
    if (searchParams.get("tab") === "creatorSearch") {
      return CreatorShowcaseContext.CampaignCreatorSearch;
    }

    if (location.pathname.includes("/creator-search")) {
      return CreatorShowcaseContext.CreatorSearch;
    }
    if (activeTab === "selected") {
      return CreatorShowcaseContext.CreatorDiscoveryList;
    }
    return CreatorShowcaseContext.CreatorDiscoverySearch;
  };

  // This useEffect is used to get profile data at the beginning and after social provider change
  useEffect(() => {
    if (selectedSocialProvider.value !== socialProvider) openProfile();
  }, [selectedSocialProvider]);

  const updateProfileDataManually = async (
    isAddedToCreatorDatabase = false,
  ) => {
    setUpdatingProfileData(true);
    const wsGlobalTaskUuid = await getProfileData(socialProvider);

    if (isAddedToCreatorDatabase) {
      if (wsGlobalTaskUuid) {
        setActiveProfileTab(ActiveProfileDataTab.General);
      }
    }

    setUpdatingProfileData(false);
    setShouldDisplayUpdateProfileDataButton(false);
  };

  useEffect(() => {
    setProfileData(null);
    setShouldSync(false);
    setSelectedSocialProvider({
      value: socialProvider,
      label: (
        <IDHFormattedMessage
          id={`front_${socialProvider}`}
          defaultMessage={capitalizeFirstLetter(socialProvider)}
        />
      ),
    });
    const originParams = searchParams.get("origin");
    if (originParams) {
      getOriginData();
    }

    getProfileData(socialProvider);
  }, [socialProvider, socialUsername]);

  const dispatchDataAndNavigateToCreateNewProject = () => {
    dispatch(
      setCreateNewProjectData({
        selectedCreatorsPayload: selectedCreatorsPayload(
          selectedInfluencersUrls,
          userData,
        ),
      }),
    );
    navigateToCreateNewProject(history, activeWorkspaceUuid);
  };

  const addCreatorsToDatabase = () => {
    showToast(
      "info",
      <IDHFormattedMessage id="ws_info" defaultMessage="Info" />,
      <IDHFormattedMessage
        id="ws_adding_creators_to_the_creator_crm"
        defaultMessage="Adding creators to the Creator CRM"
      />,
    );

    const { user_data } = profileData;
    const creatorData = {
      uuid: uuid(),
      name: user_data.username,
      displayName: user_data.displayName,
      socialProfiles: Object.keys(user_data.socialProviders).map((key) => {
        const { socialProvider, socialUsername, socialProfileUrl } =
          user_data.socialProviders[key];
        return {
          socialProvider,
          socialUsername,
          socialUrl: socialProfileUrl,
        };
      }),
    };

    axios
      .post(API_URLS.createGlobalTasks, {
        wsWorkspaceUuid: activeWorkspaceUuid,
        taskType: "creator",
        wsGlobalTasksData: [creatorData],
      })
      .then(() => {
        setTimeout(() => {
          showToast(
            "success",
            <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
            <IDHFormattedMessage
              id="ws_creators_added_to_database"
              defaultMessage="Creators successfully added to Database"
            />,
          );
          updateProfileDataManually(true);
          refreshAudience();
        }, 2000);
      })
      .catch(() => {
        showErrorToast();
      });
  };

  const handleAddToAudience = async () => {
    await setSelectedInfluencers([profileData.hashUserId]);
    await addToAudience();
    showToast(
      "success",
      <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
      <IDHFormattedMessage
        id="ws_creator_added_to_list"
        defaultMessage="Creator successfully added to list"
      />,
    );
  };

  const deleteInfluencerFromList = () => {
    dispatch(deleteInfluencersFromList([profileData?.hashUserId], listId));
    dispatch(getInfluencersListData(listId));
    hidePane();
  };

  const socialProfiles = originProfile?.user_data
    ? Object.keys(originProfile.user_data.socialProviders).map((e) => ({
        ...originProfile.user_data.socialProviders[e],
      }))
    : [];

  const renderTab = () => {
    if (!socialProfiles?.length > 0) {
      return <Loader />;
    }

    switch (activeProfileTab) {
      case ActiveProfileDataTab.General:
        return (
          <GlobalData
            taskId={globalTaskDetails.uuid}
            metadata={globalTaskDetails.metadata}
            taskOwner={taskOwner}
            dateCreated={dateCreated}
          />
        );

      case ActiveProfileDataTab.SocialData:
        return (
          <TaskSocialData
            hasAnySocialProfile={socialProfiles.length > 0}
            socialProfiles={socialProfiles}
            data={{ uuid: "", title: "" }}
            initialProfileData={profileData}
          />
        );

      case ActiveProfileDataTab.Activity:
        return <GlobalActivityTab taskId={globalTaskDetails.uuid} />;

      case ActiveProfileDataTab.Performance:
        return (
          <Performance
            context={PerformanceContext.GlobalTaskDetails}
            taskId={globalTaskDetails.uuid}
            data={performanceData}
            setData={setPerformanceData}
            filters={performanceFilters}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (paneState) {
      case "origin":
        return profileData ? (
          <>
            <PaneTopbar>
              <PaneTopbarCol>
                <UserInfo
                  userData={profileData.user_data}
                  creatorSearchSelectedProviders={
                    searchFilters?.selectedProviders
                  }
                />
              </PaneTopbarCol>
              <PaneTopbarCol className="pane-top-bar__col--right">
                <PaneTopbarToolbar hidePane={hidePane}>
                  <Dropdown toRight>
                    {(isOpen, setIsOpen) => (
                      <>
                        <Button
                          size="small"
                          variant="blue"
                          onClick={() => setIsOpen((prev) => !prev)}
                        >
                          <PlusIcon className="icon-left" />
                          <IDHFormattedMessage
                            id="front_add_to"
                            defaultMessage="Add to..."
                          />
                        </Button>
                        <DropdownMenu isOpen={isOpen}>
                          <DropdownMenuItem
                            onClick={() => {
                              addCreatorsToDatabase();
                              setIsOpen(false);
                            }}
                            disabled={profileData.wsGlobalTaskUuid}
                          >
                            <CreatorIcon />
                            <IDHFormattedMessage
                              id="front_creator_database"
                              defaultMessage="Creator Database"
                            />
                            {profileData.wsGlobalTaskUuid && <TickIcon />}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setShowModalAddCreatorsToWorkspaceProject(true);
                              setIsOpen(false);
                            }}
                          >
                            <RocketIcon />
                            <IDHFormattedMessage
                              id="front_campaign"
                              defaultMessage="Campaign"
                            />
                          </DropdownMenuItem>
                          {activeTab === "results" && listId && (
                            <DropdownMenuItem
                              onClick={handleAddToAudience}
                              disabled={disableAddToAudience}
                            >
                              <ListIcon />
                              <IDHFormattedMessage
                                id="front_creator_list"
                                defaultMessage="Creator list"
                              />
                            </DropdownMenuItem>
                          )}
                        </DropdownMenu>
                      </>
                    )}
                  </Dropdown>

                  <PreviewSettingsDropdown
                    showcaseData={{
                      history,
                      context: getContextForShowcase(),
                      creatorId,
                      listId: listId ?? undefined,
                      ids: [creatorId],
                      workspaceId: activeWorkspaceUuid,
                      projectId,
                    }}
                    linkToCopy={window.location.href}
                    removeData={
                      activeTab === "selected"
                        ? {
                            objectNames: [socialUsername],
                            removeFunction: deleteInfluencerFromList,
                          }
                        : undefined
                    }
                  />
                </PaneTopbarToolbar>

                <button
                  onClick={updateProfileDataManually}
                  disabled={isUpdatingProfileData}
                  type="button"
                  className={classNames(
                    "profile-data-pane__update-profile-data-btn button-style-reset",
                    !shouldDisplayUpdateProfileDataButton &&
                      "profile-data-pane__update-profile-data-btn--hidden",
                  )}
                >
                  <RefreshIcon
                    className={classNames(
                      isUpdatingProfileData &&
                        "profile-data-pane__update-profile-data-icon--spin",
                    )}
                  />
                  <IDHFormattedMessage
                    id="ws_refresh_profile_data"
                    defaultMessage="Refresh profile data"
                  />
                </button>
              </PaneTopbarCol>
            </PaneTopbar>

            <CreatorTags
              creatorTags={globalTaskDetails.creatorTags}
              taskId={globalTaskDetails.uuid}
              context={CreatorTagsContext.CreatorSearch}
            />

            <PaneContainer noBorder={Boolean(profileData.wsGlobalTaskUuid)}>
              {profileData.wsGlobalTaskUuid && (
                <Tabs>
                  <Tab
                    tabText={
                      <IDHFormattedMessage
                        id="ws_general"
                        defaultMessage="General"
                      />
                    }
                    active={activeProfileTab === ActiveProfileDataTab.General}
                    onClick={() =>
                      setActiveProfileTab(ActiveProfileDataTab.General)
                    }
                  />
                  <Tab
                    tabText={
                      <IDHFormattedMessage
                        id="ws_social_data"
                        defaultMessage="Social data"
                      />
                    }
                    active={
                      activeProfileTab === ActiveProfileDataTab.SocialData
                    }
                    onClick={() =>
                      setActiveProfileTab(ActiveProfileDataTab.SocialData)
                    }
                  />
                  <Tab
                    tabText={
                      <IDHFormattedMessage
                        id="ws_activity"
                        defaultMessage="Activity"
                      />
                    }
                    active={activeProfileTab === ActiveProfileDataTab.Activity}
                    onClick={() =>
                      setActiveProfileTab(ActiveProfileDataTab.Activity)
                    }
                  />
                  <Tab
                    tabText={
                      <IDHFormattedMessage
                        id="ws_performance"
                        defaultMessage="Performance"
                      />
                    }
                    active={
                      activeProfileTab === ActiveProfileDataTab.Performance
                    }
                    onClick={() =>
                      setActiveProfileTab(ActiveProfileDataTab.Performance)
                    }
                  />

                  {activeProfileTab === ActiveProfileDataTab.Performance &&
                  performanceData.length ? (
                    <PerformanceFiltersButton
                      openFilters={() => setShowFilters(true)}
                      filters={performanceFilters}
                    />
                  ) : null}
                </Tabs>
              )}

              {renderTab()}
            </PaneContainer>

            {showModalAddCreatorsToWorkspaceProject && (
              <AddCreatorsCDTWorkspaceProjectModal
                onClose={() => setShowModalAddCreatorsToWorkspaceProject(false)}
                activeWorkspaceUuid={activeWorkspaceUuid}
                selectedCreators={generateSelectedInfluencersWithSocialProfiles(
                  selectedInfluencersUrls,
                  userData,
                )}
                openCreateNewProjectModal={
                  dispatchDataAndNavigateToCreateNewProject
                }
              />
            )}

            <PerformanceFilters
              setFiltersVisible={setShowFilters}
              filtersVisible={showFilters}
              performanceData={performanceData}
              filters={performanceFilters}
              setFilters={setPerformanceFilters}
            />
          </>
        ) : (
          <Loader />
        );
      case "notFoundWithOrigin":
        return originProfile ? (
          <>
            <PaneTopbar>
              <PaneTopbarCol>
                <UserInfo
                  userData={originProfile.user_data}
                  creatorSearchSelectedProviders={
                    searchFilters?.selectedProviders ?? []
                  }
                />
              </PaneTopbarCol>
              <PaneTopbarCol className="pane-top-bar__col--right">
                <PaneTopbarToolbar hidePane={hidePane}>
                  {socialProvider == "snapchat" && (
                    <ExportDropdown hashUserId={originProfile.hashUserId} />
                  )}
                </PaneTopbarToolbar>
                <SocialProviderSelect
                  socialProfiles={socialProfiles}
                  setSelectedSocialProvider={setSelectedSocialProvider}
                  selectedSocialProvider={selectedSocialProvider}
                />
              </PaneTopbarCol>
            </PaneTopbar>

            <UserNotFound
              goBack={() => {
                setProfileData(null);
                history.goBack();
                openProfile();
              }}
            />
          </>
        ) : (
          <Loader />
        );
      case "notFound":
        return (
          <UserNotFound
            closePane={hidePane}
            goBack={() => {
              setProfileData(null);
              history.goBack();
              openProfile();
            }}
          />
        );
      case "loading":
      default:
        return <Loader />;
    }
  };

  useEffect(() => {
    return () => {
      dispatch(clearGlobalTaskDetails());
    };
  }, [dispatch]);

  return (
    <Pane
      showPane={showPane}
      hidePane={hidePane}
      width={PANE_WIDTH}
      className="profile-data-pane"
    >
      {renderContent()}
    </Pane>
  );
}

export { ProfileDataPane };
